// pipe (scan intake): the global Scan dialog — a scan-gun-first lookup box (AUTOFOCUSED input;
// gun scanners type the code and send Enter) plus an optional camera path (BarcodeDetector,
// where the platform provides it). PURE view (the records.tsx / records-view.tsx seam rule):
// the DI wiring lives in scan-button.tsx — this file receives `lookup` (ErpService.scanLookup),
// `transition` (ErpService.transition) and `onNavigate` (workbench.open) as props so the Vitest
// spec drives it without the runtime.
//
// Resolution (scan-hardening): 0 matches → the localized not-found line; 1 match → the inline
// RESULT CARD (the scanned record's redacted projection + the SHARED TransitionAffordance —
// scan-to-act works from ANY station, because the server scan endpoint is deliberately broad
// while every act stays server-gated by `allowed_transitions`); N matches → a pick list whose
// pick shows the same card. The card's "Show in list" rides the ONE type→route SSOT
// (erpResultRoute — the same mapping the global-search palette uses); "Print label" reprints the
// scanned code through the pure labels module. The camera NEVER leaks: every acquired track is
// stopped on hit, on stop, on close, and on unmount.
import { Button, Input, Modal } from '@affine/component';
import { humanizeToken } from '@affine/erp-ui';
import { useI18n } from '@affine/i18n';
import { useCallback, useEffect, useRef, useState } from 'react';

import { ErpInfoTip } from '../../desktop/pages/workspace/erp-info-tip';
import { stageLabel } from '../../desktop/pages/workspace/ops/stage-label';
import { TransitionAffordance } from '../../desktop/pages/workspace/records/transition-affordance';
import type { RecordRow } from '../../modules/erp';
import { type LabelItem, printLabels } from '../../modules/labels';
import {
  type BarcodeDetectorLike,
  cameraDecodeSupported,
  createBarcodeDetector,
} from '../../modules/labels/barcode-detector';
import {
  erpListRoute,
  hasPassport,
  isLegacyRow,
  passportRoute,
} from '../../modules/quicksearch/impls/erp-route';
import * as styles from './scan-dialog.css';

export interface ScanDialogProps {
  open: boolean;
  onClose: () => void;
  /** The ErpService.scanLookup delegate (server-side Erp gate + partition + redaction). */
  lookup: (code: string) => Promise<RecordRow[]>;
  /** The ErpService.transition delegate — the card's advance action (server-gated; the response
   *  is the SAME one-serializer projection incl. its own fresh `allowed_transitions`). */
  transition: (id: string, to: string) => Promise<RecordRow>;
  /** IP6.3 put-away: post ONE audited `storage_placement` create for (scanned item, scanned bin)
   *  — `placementCreateBody` is the exact body. The server derives the placement's site from the
   *  bin and refuses a cross-facility bin with an explicit message (§4.10 — a real floor error);
   *  that message surfaces inline. Absent ⇒ the "Put away" affordance never renders. */
  createPlacement?: (item: RecordRow, bin: RecordRow) => Promise<RecordRow>;
  /** IP7.1 set-host: PATCH the scanned drive's `host_asset_id` to the NEXT-scanned asset through
   *  the ONE updateRecord chokepoint (`hostAssetPatchBody` is the exact body — a shallow top-level
   *  merge, so ONLY that key changes). The server re-validates the parent (`revalidate_parent`:
   *  visible, not self, no un-sanitized drive under a data-bearing-terminal ancestor) — its
   *  refusal text surfaces inline VERBATIM. The response is the same one-serializer projection as
   *  a list row and REPLACES the card row in place. Absent ⇒ the affordance never renders. */
  setHostAsset?: (drive: RecordRow, asset: RecordRow) => Promise<RecordRow>;
  /** IP7.2 add-to-pallet: POST ONE `contained_in` relation for (scanned item, scanned container)
   *  through the cookie /api/relations twin of the one link_core chokepoint —
   *  `containRelationBody` is the exact body. The server refuses linking an un-sanitized drive
   *  under a data-bearing terminal (`link_data_bearing_guard`) — its message surfaces inline
   *  VERBATIM. Absent ⇒ the affordance never renders. */
  addToPallet?: (item: RecordRow, container: RecordRow) => Promise<unknown>;
  /** The workbench navigation delegate (workbench.open). */
  onNavigate: (to: string) => void;
}

/** What the dialog is showing under the (always-live) scan box. The `card`'s `placedIn` /
 *  `hostSet` / `palletized` are the two-scan confirmations ("Placed in R06-A-03", "Host set to
 *  …", "Added to PAL-0007") — the same card, one extra status line each. The `putaway` /
 *  `sethost` / `pallet` arms hold the scanned ITEM while the NEXT scan is expected to be the
 *  arm's target (bin / host asset / container); `busy` is the in-flight write — while it is set
 *  the scan box is dead and further scans are refused, because the arm re-renders as the SAME
 *  prompt during the await and a gun double-fire would otherwise mint a SECOND audited write for
 *  the same pair. */
type ScanView =
  | { kind: 'none' }
  | { kind: 'empty'; code: string }
  | { kind: 'picks'; code: string; rows: RecordRow[] }
  | {
      kind: 'card';
      code: string;
      row: RecordRow;
      placedIn?: string;
      hostSet?: string;
      palletized?: string;
    }
  | { kind: 'putaway'; code: string; row: RecordRow; error?: string; busy?: boolean }
  | { kind: 'sethost'; code: string; row: RecordRow; error?: string; busy?: boolean }
  | { kind: 'pallet'; code: string; row: RecordRow; error?: string; busy?: boolean };

/** The two-scan arms share one state machine (item held, next scan = the arm's target). */
type PendingArm = Extract<ScanView, { kind: 'putaway' | 'sethost' | 'pallet' }>;
const isPendingArm = (v: ScanView): v is PendingArm =>
  v.kind === 'putaway' || v.kind === 'sethost' || v.kind === 'pallet';

/** IP6.3: the item kinds a floor operator shelves — the "Put away" affordance renders only for
 *  these. A bin (`storage_location`) or an order is never itself put away. */
export const PUTAWAY_ITEM_TYPES: readonly string[] = [
  'part',
  'commodity_lot',
  'asset',
  'drive',
  'inventory_item',
];

/** IP7.2: the item kinds that ride a pallet/container — the "Add to pallet" affordance renders
 *  only for these (a container is never palletized onto itself; an order is not a physical item). */
export const PALLET_ITEM_TYPES: readonly string[] = [
  'part',
  'commodity_lot',
  'asset',
  'drive',
];

/** IP7.1: does the scanned drive already NAME its host machine? A redaction-masked value counts
 *  as PRESENT (`host_asset_id_redacted: true` means it HAS one this reader may not see — the
 *  affordance must never read a mask as absence and offer to overwrite it). */
export function driveHasHostAsset(row: RecordRow): boolean {
  const v = row.data?.host_asset_id;
  return (
    (typeof v === 'string' && v !== '') ||
    row.data?.host_asset_id_redacted === true
  );
}

/** PURE: the EXACT PATCH body for IP7.1 "Set host asset" — ONLY the one key rides the server's
 *  shallow top-level `data` merge (every other stored field is preserved), and `title: null`
 *  carries the stored title forward (the update core's `Option<String>` contract). The server
 *  (`revalidate_parent`) re-checks visibility / self / terminal-ancestor — never the client. */
export function hostAssetPatchBody(asset: RecordRow): {
  title: null;
  data: { host_asset_id: string };
} {
  return { title: null, data: { host_asset_id: asset.id } };
}

/** PURE: the EXACT cookie POST /api/relations body for IP7.2 — the NEW `contained_in` label
 *  (never `contains`; the plan reserves the direction item → container), no relation `data`. */
export function containRelationBody(
  item: RecordRow,
  container: RecordRow
): { src: string; dst: string; rel: 'contained_in' } {
  return { src: item.id, dst: container.id, rel: 'contained_in' };
}

/** PURE: the ONE audited create body for a put-away (§4.10 — a placement is a CREATE, never a
 *  mutation; append-only, so a mis-scan is corrected by the next placement, never an edit). The
 *  title is the bin's printed `label_code` (non-sensitive — our own opaque token). NO facility_id:
 *  the server derives it from the bin and refuses a cross-facility placement explicitly. */
export function placementCreateBody(
  item: RecordRow,
  bin: RecordRow
): { type: string; title: string | null; data: Record<string, unknown> } {
  const label = bin.data?.label_code;
  return {
    type: 'storage_placement',
    title:
      typeof label === 'string' && label
        ? label
        : bin.title && !bin.title_redacted
          ? bin.title
          : null,
    data: { item_ref: item.id, location_id: bin.id },
  };
}

/** PURE: what a confirmation calls the scanned TARGET (a bin, a host asset, a container) — its
 *  printed `label_code`, then a visible title, then the code the operator just scanned (which is
 *  how the row resolved in the first place). Was `binDisplayLabel` before IP7 made the same
 *  fallback chain serve all three two-scan arms. */
export function scannedTargetLabel(code: string, target: RecordRow): string {
  const label = target.data?.label_code;
  if (typeof label === 'string' && label) return label;
  if (target.title && !target.title_redacted) return target.title;
  return code;
}

// The grid's title fallback contract (records-view rowTitle): a redaction-flagged title NEVER
// surfaces its raw value; a null title renders the localized untitled label.
const rowTitle = (t: ReturnType<typeof useI18n>, r: RecordRow) => {
  if (r.title_redacted) return t['com.affine.pipe.records.redacted']();
  return r.title ?? t['com.affine.pipe.records.untitled']({ type: r.type });
};

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface CardField {
  key: string;
  value: string;
  redacted: boolean;
}

/** PURE: the card's visible field lines from an already-redacted `data` projection. A masked
 *  scalar arrives as `null` + a `<key>_redacted: true` sibling (the ONE serializer's contract) —
 *  it renders as the localized redacted placeholder, NEVER a value. Entity-ref noise (bare-UUID
 *  values — join keys) and non-primitives are dropped BY VALUE, never by key name (a key-name
 *  heuristic like `*_id` would drop `tag_id`, the floor's PRIMARY identifier). Values render RAW
 *  (a humanized serial would corrupt what the operator just scanned).
 *
 *  NOTHING is truncated (review finding). This used to keep only the first 10 entries, and the
 *  server's `data` serializes ALPHABETICALLY (serde_json without `preserve_order` — a BTreeMap),
 *  which was harmless while every scannable type emitted a handful of keys and silently
 *  destructive the moment the ROMS import routed 14-key `part`/`commodity_lot` rows here: the ten
 *  slots filled with `barcode…legacy_value_target` bookkeeping and `quantity`, `serial_number`
 *  and `uom` — the identity of a bulk-stock bin — never rendered at all. Worse for the more
 *  privileged reader, since a `Field(Financials)` holder keeps the four money keys that push them
 *  out. The list is layout-bounded by its own scroll container (`cardFields` css), so a cap could
 *  only ever discard information, never protect the card. */
export function cardFields(data: Record<string, unknown>): CardField[] {
  const lines: CardField[] = [];
  for (const [key, value] of Object.entries(data)) {
    if (key.endsWith('_redacted')) continue;
    if (data[`${key}_redacted`] === true) {
      lines.push({ key, value: '', redacted: true });
      continue;
    }
    if (value === null || value === undefined || value === '') continue;
    const kind = typeof value;
    if (kind !== 'string' && kind !== 'number' && kind !== 'boolean') continue;
    if (kind === 'string' && UUID_RE.test(value as string)) continue;
    lines.push({ key, value: String(value), redacted: false });
  }
  return lines;
}

/** PURE: the label to (re)print for a scanned row — the code itself, plus the row title as an
 *  extra line ONLY when it is visible, non-redacted information beyond the code (the intake
 *  queue's rule — a redacted value never reaches paper). */
export function labelForRow(code: string, row: RecordRow): LabelItem {
  return row.title && !row.title_redacted && row.title !== code
    ? { code, title: row.title }
    : { code };
}

const ScanResultCard = ({
  code,
  row,
  placedIn,
  hostSet,
  palletized,
  onAdvance,
  onOpenList,
  onOpenPassport,
  onPutAway,
  onSetHost,
  onAddToPallet,
}: {
  code: string;
  row: RecordRow;
  /** IP6.3: the bin this item was JUST placed into — renders the confirmation line. */
  placedIn?: string;
  /** IP7.1: the host asset this drive was JUST attached to — renders the confirmation line. */
  hostSet?: string;
  /** IP7.2: the container this item was JUST palletized into — renders the confirmation line. */
  palletized?: string;
  onAdvance: (to: string) => Promise<unknown>;
  onOpenList: () => void;
  /** IP2.6: open the item's full history (`/records/:id`). Absent for a type with no passport —
   *  the decision rides the ONE `hasPassport` SSOT in erp-route.ts, never a predicate here. */
  onOpenPassport?: () => void;
  /** IP6.3: enter the put-away arm (scan a bin next). Absent for a non-shelvable type or a
   *  container with no createPlacement delegate — the button then never renders. */
  onPutAway?: () => void;
  /** IP7.1: enter the set-host arm (scan the host machine's asset next). Absent for a non-drive,
   *  a drive that already names its host, or no wired delegate — the button never renders. */
  onSetHost?: () => void;
  /** IP7.2: enter the add-to-pallet arm (scan a container next). Absent for a non-palletizable
   *  type or no wired delegate — the button never renders. */
  onAddToPallet?: () => void;
}) => {
  const t = useI18n();
  const fields = cardFields(row.data ?? {});
  return (
    <div className={styles.card} data-testid="pipe-scan-card">
      <div className={styles.cardHeader}>
        <span className={styles.matchType}>{humanizeToken(row.type)}</span>
        {row.stage ? (
          <span className={styles.cardStage}>{stageLabel(t, row.stage)}</span>
        ) : null}
      </div>
      <div className={styles.cardTitle}>{rowTitle(t, row)}</div>
      {placedIn ? (
        <p
          className={styles.putawayDone}
          role="status"
          data-testid="pipe-scan-putaway-done"
        >
          {t['com.affine.pipe.scan.putaway.done']({ bin: placedIn })}
        </p>
      ) : null}
      {hostSet ? (
        <p
          className={styles.putawayDone}
          role="status"
          data-testid="pipe-scan-sethost-done"
        >
          {t['com.affine.pipe.scan.sethost.done']({ asset: hostSet })}
        </p>
      ) : null}
      {palletized ? (
        <p
          className={styles.putawayDone}
          role="status"
          data-testid="pipe-scan-pallet-done"
        >
          {t['com.affine.pipe.scan.pallet.done']({ container: palletized })}
        </p>
      ) : null}
      {fields.length > 0 ? (
        <dl className={styles.cardFields}>
          {fields.map(f => (
            <div key={f.key} className={styles.cardFieldRow}>
              <dt className={styles.cardFieldKey}>{humanizeToken(f.key)}</dt>
              <dd className={styles.cardFieldValue}>
                {f.redacted ? t['com.affine.pipe.records.redacted']() : f.value}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}
      <div className={styles.cardActions}>
        <TransitionAffordance
          allowedTransitions={row.allowed_transitions ?? []}
          onAdvance={onAdvance}
        />
        {/* IP6.3: shelve the item — the next scan is expected to be a bin label. */}
        {onPutAway ? (
          <Button
            variant="secondary"
            data-testid="pipe-scan-putaway"
            onClick={onPutAway}
          >
            {t['com.affine.pipe.scan.putaway']()}
          </Button>
        ) : null}
        {/* IP7.1: name the drive's host machine — the next scan is expected to be its asset tag. */}
        {onSetHost ? (
          <Button
            variant="secondary"
            data-testid="pipe-scan-sethost"
            onClick={onSetHost}
          >
            {t['com.affine.pipe.scan.sethost']()}
          </Button>
        ) : null}
        {/* IP7.2: palletize the item — the next scan is expected to be a container label. */}
        {onAddToPallet ? (
          <Button
            variant="secondary"
            data-testid="pipe-scan-pallet"
            onClick={onAddToPallet}
          >
            {t['com.affine.pipe.scan.pallet']()}
          </Button>
        ) : null}
        {/* IP2.6, ADDITIVE: the card keeps everything it had (its full, uncapped field list is
            what the operator holding the item reads) and gains ONE primary link to the item's
            whole life. The card never grows timeline / section / spine content — it links. */}
        {onOpenPassport ? (
          <Button
            variant="primary"
            data-testid="pipe-scan-passport"
            onClick={onOpenPassport}
          >
            {t['com.affine.pipe.scan.full-history']()}
          </Button>
        ) : null}
      </div>
      <div className={styles.cardActions}>
        <Button
          variant="secondary"
          data-testid="pipe-scan-print"
          onClick={() => printLabels([labelForRow(code, row)])}
        >
          {t['com.affine.pipe.scan.print-label']()}
        </Button>
        <Button
          variant="secondary"
          data-testid="pipe-scan-open-list"
          onClick={onOpenList}
        >
          {t['com.affine.pipe.scan.open-list']()}
        </Button>
      </div>
    </div>
  );
};

export const ScanDialog = ({
  open,
  onClose,
  lookup,
  transition,
  createPlacement,
  setHostAsset,
  addToPallet,
  onNavigate,
}: ScanDialogProps) => {
  const t = useI18n();
  const [value, setValue] = useState('');
  const [view, setView] = useState<ScanView>({ kind: 'none' });
  const [cameraOn, setCameraOn] = useState(false);
  const [cameraFailed, setCameraFailed] = useState(false);

  // Latest-wins guard for in-flight lookups (the standing fetch rule): a slow earlier scan must
  // never land late and clobber a newer scan's outcome.
  const seqRef = useRef(0);
  // The view at resolve time, WITHOUT putting `view` in resolveCode's deps — that would restart
  // the camera effect (and re-create its detector) on every card/putaway state change. Only the
  // put-away branch reads it.
  const viewRef = useRef<ScanView>(view);
  useEffect(() => {
    viewRef.current = view;
  }, [view]);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const disposedRef = useRef(false);

  const stopCamera = useCallback(() => {
    for (const track of streamRef.current?.getTracks() ?? []) {
      track.stop();
    }
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraOn(false);
  }, []);

  // Unmount: invalidate in-flight work and release the camera (never leak a track).
  useEffect(() => {
    disposedRef.current = false;
    return () => {
      disposedRef.current = true;
      seqRef.current++;
      stopCamera();
    };
  }, [stopCamera]);

  const close = useCallback(() => {
    stopCamera();
    onClose();
  }, [onClose, stopCamera]);

  const openInList = useCallback(
    (row: RecordRow, code: string) => {
      // The ONE type→route SSOT, LIST arm (accounts → Customers, staged types → their
      // Operations worklist, IMPORTED rows + everything else → Records), pre-seeded with the
      // scanned code (`?q=`). A scanned ROMS part/lot is legacy, so it lands in Records — the
      // live worklist excludes legacy rows server-side and would show the operator nothing.
      // Deliberately `erpListRoute`, not `erpResultRoute`: "Show in list" must reach a LIST even
      // for an item type, or it collapses onto the card's own "Full history" action.
      onNavigate(
        erpListRoute({
          type: row.type,
          id: row.id,
          stage: row.stage ?? null,
          seed: code,
          legacy: isLegacyRow(row.data),
        })
      );
      close();
    },
    [close, onNavigate]
  );

  // IP2.6: the item's full history. Same navigate-then-close shape as "Show in list".
  const openPassport = useCallback(
    (row: RecordRow) => {
      onNavigate(passportRoute(row.id));
      close();
    },
    [close, onNavigate]
  );

  // Per-arm second-scan resolution: the target TYPE and the two honest failure lines. Putaway's
  // strings stay byte-identical to IP6; the two IP7 arms mirror the shape.
  const armResolution = useCallback(
    (kind: PendingArm['kind'], code: string) => {
      switch (kind) {
        case 'putaway':
          return {
            targetType: 'storage_location',
            ambiguous: t['com.affine.pipe.scan.putaway.ambiguous-bin']({
              code,
            }),
            notTarget: t['com.affine.pipe.scan.putaway.not-a-bin']({ code }),
          };
        case 'sethost':
          return {
            targetType: 'asset',
            ambiguous: t['com.affine.pipe.scan.sethost.ambiguous-asset']({
              code,
            }),
            notTarget: t['com.affine.pipe.scan.sethost.not-an-asset']({
              code,
            }),
          };
        case 'pallet':
          return {
            targetType: 'container',
            ambiguous: t['com.affine.pipe.scan.pallet.ambiguous-container']({
              code,
            }),
            notTarget: t['com.affine.pipe.scan.pallet.not-a-container']({
              code,
            }),
          };
      }
    },
    [t]
  );

  const resolveCode = useCallback(
    async (raw: string) => {
      const code = raw.trim();
      if (!code) return;
      // An arm's write is in flight: refuse the scan OUTRIGHT (before the seq bump, so the
      // in-flight write's own continuation stays current). Bumping the seq here would abandon
      // the first write's confirmation while its audited row still landed — and resolving the
      // scan would re-enter the arm and mint a SECOND write (the double-fire gun case).
      if (isPendingArm(viewRef.current) && viewRef.current.busy) return;
      const seq = ++seqRef.current;
      // In a two-scan arm the ITEM context must survive the target scan, so the prompt stays up
      // (with any stale error cleared) instead of blanking to `none`.
      const pending = isPendingArm(viewRef.current) ? viewRef.current : null;
      if (pending) setView({ ...pending, error: undefined });
      else setView({ kind: 'none' });
      let rows: RecordRow[];
      try {
        rows = await lookup(code);
      } catch {
        // Fail soft: an errored lookup reads as "nothing matches" (re-scan retries) — in an arm
        // that is "not a <target>", and the arm stays so the operator re-scans.
        if (seqRef.current !== seq) return;
        setValue('');
        if (pending) {
          setView({
            ...pending,
            error: armResolution(pending.kind, code).notTarget,
          });
        } else {
          setView({ kind: 'empty', code });
        }
        return;
      }
      if (seqRef.current !== seq) return;
      // Scanner-friendly: the next gun read types into a clean box (a leftover value would
      // concatenate with the next scan into garbage).
      setValue('');
      if (pending) {
        // The second scan of the pair. The target resolves BY TYPE, never by count: the scan
        // endpoint is deliberately broad, so the target can come back alongside an item that
        // shares the code, and a duplicated label can resolve rows at SEVERAL sites. Exactly
        // one row of the arm's target type (mixed company or not) is usable; two-plus is an
        // honest ambiguity ("pin a site"), never "not a <target>"; and only a result with NO
        // target-typed row at all is genuinely not one.
        const arm = armResolution(pending.kind, code);
        const targets = rows.filter(r => r.type === arm.targetType);
        if (targets.length > 1) {
          setView({ ...pending, busy: false, error: arm.ambiguous });
          return;
        }
        const target = targets.length === 1 ? targets[0] : null;
        const delegate =
          pending.kind === 'putaway'
            ? createPlacement
            : pending.kind === 'sethost'
              ? setHostAsset
              : addToPallet;
        if (!target || !delegate) {
          setView({ ...pending, busy: false, error: arm.notTarget });
          return;
        }
        // Busy BEFORE the await: the prompt re-renders with the in-flight line, the scan box
        // goes dead, and the guard at the top refuses any scan until this write settles.
        setView({ ...pending, busy: true, error: undefined });
        let updated: RecordRow | null = null;
        try {
          const res = await delegate(pending.row, target);
          // IP7.1: the PATCH response is the SAME one-serializer projection as a list row —
          // it REPLACES the card row in place (the standing splice-in-place rule), so the set
          // host_asset_id immediately retires the affordance.
          if (pending.kind === 'sethost') updated = res as RecordRow;
        } catch (e) {
          // The audited write refused (cross-facility bin, revalidate_parent, the data-bearing
          // link guard, …) — surface the server's own words inline VERBATIM and stay in the
          // arm, busy cleared.
          if (seqRef.current !== seq || disposedRef.current) return;
          setView({
            ...pending,
            busy: false,
            error:
              e instanceof Error && e.message ? e.message : arm.notTarget,
          });
          return;
        }
        if (seqRef.current !== seq || disposedRef.current) return;
        setView({
          kind: 'card',
          code: pending.code,
          row: updated ?? pending.row,
          ...(pending.kind === 'putaway'
            ? { placedIn: scannedTargetLabel(code, target) }
            : pending.kind === 'sethost'
              ? { hostSet: scannedTargetLabel(code, target) }
              : { palletized: scannedTargetLabel(code, target) }),
        });
        return;
      }
      if (rows.length === 0) {
        setView({ kind: 'empty', code });
      } else if (rows.length === 1) {
        setView({ kind: 'card', code, row: rows[0] });
      } else {
        setView({ kind: 'picks', code, rows });
      }
    },
    [addToPallet, armResolution, createPlacement, lookup, setHostAsset]
  );

  // The card's advance action: the transition response is the SAME one-serializer projection
  // (incl. its own fresh `allowed_transitions`) — it REPLACES the card row in place, so the next
  // legal step renders immediately. Errors reject through to the shared affordance's inline line.
  const advance = useCallback(
    async (code: string, row: RecordRow, to: string) => {
      const next = await transition(row.id, to);
      if (!disposedRef.current) setView({ kind: 'card', code, row: next });
      return next;
    },
    [transition]
  );

  // Camera path: shown wherever camera CAPTURE exists — decoding is the native BarcodeDetector
  // (Chromium/Android) or the lazy ZXing fallback (iOS/WebKit — mobile-first), resolved by the
  // ONE barcode-detector adapter when the camera starts. No capture ⇒ the localized "use the
  // gun / type it" hint renders instead.
  const cameraSupported = cameraDecodeSupported();

  const startCamera = useCallback(async () => {
    if (streamRef.current) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: false,
      });
      if (disposedRef.current) {
        // Unmounted while the permission prompt was up — release immediately (never leak).
        for (const track of stream.getTracks()) track.stop();
        return;
      }
      streamRef.current = stream;
      setCameraOn(true);
    } catch {
      // Denied / no device: degrade to the unsupported hint (fail soft, no camera affordance).
      setCameraFailed(true);
    }
  }, []);

  // While the camera is on: feed the stream into the <video>, resolve the platform decoder
  // (native or the lazy ZXing chunk) and poll it (~200ms); the FIRST hit stops the camera (all
  // tracks) and resolves through the SAME path as a typed code. `live` gates late detect()
  // settlements so a hit can never double-fire; a decoder that cannot be loaded (offline
  // mid-session) releases the camera and degrades to the gun/type hint.
  useEffect(() => {
    if (!cameraOn) return;
    const video = videoRef.current;
    const stream = streamRef.current;
    if (!video || !stream) return;
    try {
      video.srcObject = stream;
      const playing = video.play() as Promise<void> | undefined;
      playing?.catch(() => {});
    } catch {
      // A non-media DOM (tests) — the detect poll below still runs against the element.
    }
    let live = true;
    let detecting = false;
    let detector: BarcodeDetectorLike | null = null;
    createBarcodeDetector()
      .then(d => {
        if (!live) return;
        if (!d) {
          stopCamera();
          setCameraFailed(true);
          return;
        }
        detector = d;
      })
      .catch(() => {
        if (live) {
          stopCamera();
          setCameraFailed(true);
        }
      });
    const timer = setInterval(() => {
      if (!live || detecting || !detector) return;
      detecting = true;
      detector
        .detect(video)
        .then(hits => {
          detecting = false;
          if (!live) return;
          const hit = hits.find(h => h.rawValue);
          if (hit) {
            live = false;
            stopCamera();
            // resolveCode resolves its own failures into the not-found state.
            resolveCode(hit.rawValue).catch(() => {});
          }
        })
        .catch(() => {
          detecting = false;
        });
    }, 200);
    return () => {
      live = false;
      clearInterval(timer);
    };
  }, [cameraOn, resolveCode, stopCamera]);

  const heading = t['com.affine.pipe.scan.title']();

  return (
    <Modal
      open={open}
      onOpenChange={next => {
        if (!next) close();
      }}
      title={heading}
      width={480}
      contentOptions={{ 'aria-label': heading }}
    >
      <div className={styles.dialog}>
        <Input
          autoFocus
          autoSelect
          value={value}
          onChange={setValue}
          onEnter={code => void resolveCode(code)}
          // Dead while an arm's write is in flight (the resolveCode guard is the real
          // refusal — this makes the refusal VISIBLE at the box).
          disabled={isPendingArm(view) && !!view.busy}
          placeholder={t['com.affine.pipe.scan.placeholder']()}
          aria-label={heading}
          data-testid="pipe-scan-input"
          endFix={
            <ErpInfoTip
              content={t['com.affine.pipe.tooltip.scan']()}
              data-testid="erp-scan-info-tip"
            />
          }
        />

        {view.kind === 'empty' ? (
          <p className={styles.hint} role="status">
            {t['com.affine.pipe.scan.not-found']({ code: view.code })}
          </p>
        ) : null}

        {view.kind === 'picks' ? (
          <div className={styles.matches}>
            <p className={styles.hint} role="status">
              {t['com.affine.pipe.scan.matches']({
                count: String(view.rows.length),
              })}
            </p>
            {view.rows.map(row => (
              <button
                key={row.id}
                type="button"
                className={styles.matchRow}
                data-testid="pipe-scan-match"
                onClick={() => setView({ kind: 'card', code: view.code, row })}
              >
                <span className={styles.matchType}>{row.type}</span>
                <span className={styles.matchTitle}>{rowTitle(t, row)}</span>
              </button>
            ))}
          </div>
        ) : null}

        {view.kind === 'card' ? (
          <ScanResultCard
            code={view.code}
            row={view.row}
            placedIn={view.placedIn}
            hostSet={view.hostSet}
            palletized={view.palletized}
            onAdvance={to => advance(view.code, view.row, to)}
            onOpenList={() => openInList(view.row, view.code)}
            onOpenPassport={
              hasPassport(view.row.type)
                ? () => openPassport(view.row)
                : undefined
            }
            onPutAway={
              createPlacement && PUTAWAY_ITEM_TYPES.includes(view.row.type)
                ? () =>
                    setView({
                      kind: 'putaway',
                      code: view.code,
                      row: view.row,
                    })
                : undefined
            }
            onSetHost={
              setHostAsset &&
              view.row.type === 'drive' &&
              !driveHasHostAsset(view.row)
                ? () =>
                    setView({
                      kind: 'sethost',
                      code: view.code,
                      row: view.row,
                    })
                : undefined
            }
            onAddToPallet={
              addToPallet && PALLET_ITEM_TYPES.includes(view.row.type)
                ? () =>
                    setView({
                      kind: 'pallet',
                      code: view.code,
                      row: view.row,
                    })
                : undefined
            }
          />
        ) : null}

        {isPendingArm(view) ? (
          // The two-scan arms share ONE renderer: the held item, the arm's prompt (or its
          // in-flight line), any inline refusal, and Cancel. The per-arm testids keep the IP6
          // putaway ids byte-identical (`pipe-scan-putaway-arm` / `-placing` / `-error` /
          // `-cancel`) while the IP7 arms get their own (`sethost` / `pallet`).
          <div className={styles.card} data-testid={`pipe-scan-${view.kind}-arm`}>
            <div className={styles.cardHeader}>
              <span className={styles.matchType}>
                {humanizeToken(view.row.type)}
              </span>
            </div>
            <div className={styles.cardTitle}>{rowTitle(t, view.row)}</div>
            {view.busy ? (
              // The in-flight write: one audited row is being written — say so, and offer
              // nothing (the scan box is dead and Cancel would orphan the confirmation).
              <p
                className={styles.hint}
                role="status"
                data-testid={`pipe-scan-${view.kind}-placing`}
              >
                {view.kind === 'putaway'
                  ? t['com.affine.pipe.scan.putaway.placing']()
                  : view.kind === 'sethost'
                    ? t['com.affine.pipe.scan.sethost.saving']()
                    : t['com.affine.pipe.scan.pallet.linking']()}
              </p>
            ) : (
              <p className={styles.hint} role="status">
                {view.kind === 'putaway'
                  ? t['com.affine.pipe.scan.putaway.prompt']()
                  : view.kind === 'sethost'
                    ? t['com.affine.pipe.scan.sethost.prompt']()
                    : t['com.affine.pipe.scan.pallet.prompt']()}
              </p>
            )}
            {view.error ? (
              <p
                className={styles.putawayError}
                role="alert"
                data-testid={`pipe-scan-${view.kind}-error`}
              >
                {view.error}
              </p>
            ) : null}
            <div className={styles.cardActions}>
              <Button
                variant="secondary"
                disabled={!!view.busy}
                data-testid={`pipe-scan-${view.kind}-cancel`}
                onClick={() =>
                  setView({ kind: 'card', code: view.code, row: view.row })
                }
              >
                {t['Cancel']()}
              </Button>
            </div>
          </div>
        ) : null}

        {cameraSupported && !cameraFailed ? (
          <div className={styles.cameraArea}>
            {cameraOn ? (
              <>
                <video
                  ref={videoRef}
                  className={styles.video}
                  muted
                  playsInline
                  data-testid="pipe-scan-video"
                />
                <div className={styles.cameraActions}>
                  <Button variant="secondary" onClick={stopCamera}>
                    {t['com.affine.pipe.scan.camera-stop']()}
                  </Button>
                </div>
              </>
            ) : (
              <div className={styles.cameraActions}>
                <Button
                  variant="secondary"
                  data-testid="pipe-scan-camera"
                  onClick={() => void startCamera()}
                >
                  {t['com.affine.pipe.scan.camera']()}
                </Button>
              </div>
            )}
          </div>
        ) : (
          <p className={styles.hint}>
            {t['com.affine.pipe.scan.camera-unsupported']()}
          </p>
        )}
      </div>
    </Modal>
  );
};
